import { BugReport, GridViewOutlined } from "@mui/icons-material";
import { SvgIconProps } from "@mui/material";
import { useEffect, useState } from "react";
import { DocumentIcon } from "../../../components/icons";
import FigmaLinkIcon from "../../../components/icons/FigmaIcon";
import {
  AllCrewIcon,
  ConfigurationIcon,
  ContactIcon,
  DashboardIcon,
  EndpointRequestIcon,
  MemoryIcon,
  OperationsIcon,
  UserAdminIcon
} from "../../sidebar/icons";

export const getAllAppRoutes = (): string[] => {
  return [
    "/dashboard",
    "/bookings",
    "/bookings/funeral",
    "/bookings/event",
    "/bookings/conference",
    "/bookings/church",
    "/bookings/funeral-home",
    "/bookings/other",
    "/contacts",
    "/venues",
    "/venues/add",
    "/crew-management",
    "/crew-management/all-crew",
    "/crew-management/checklists",
    "/crew-management/crew-ratings",
    "/crew-management/rates",
    "/stream-management",
    "/stream-management/add",
    "/stream-management/ten-day-forecast",
    "/gear-management",
    "/settings",
    "/configurations",
    "/configurations/manage-roles",
    "/configurations/thumbnail-template-manager",
    "/configurations/package",
    "/configurations/venues-admin",
    "/configurations/proposal-cover-page",
    "/configurations/share-messages",
    "/configurations/proposal-introduction-letter",
    "/configurations/testimonials",
    "/configurations/seo-management",
    "/configurations/components-manager",
    "/user-management",
    "/proposals",
    "/proposals/proposals-builder",
    "/proposal/service-quotation",
    "/request-for-quote",
    "/client-portal-dashboard",
    "/memory-table",
    "/autolog",
    "/sales",
    "/changelog",
    "/bug-reports",
    "/endpoint-requests",
    "/endpoint-requests/frontend",
    "/endpoint-requests/backend",
    "/site-visit",
    "/sponsorships",
    "/streamfund",
    "/referrals",
    "/companies",
    "/companies/add",
    "/courses",
    "/courses/overview",
    "/courses/create-course",
    "/assessments",
    "/badges",
    "/youtube-channels",
    "/bulk-crew-assignment",
    "/chema/insights",
    "/memory-book-dashboard",
  ];
};

export type AppItem = {
  label: string;
  icon: React.ComponentType<SvgIconProps>;
  route?: string;
  action?: "OPEN_TOOLS_DRAWER";
  color: string;
  children?: { label: string; route: string }[];
  isCustom?: boolean;
};

export const apps: AppItem[] = [
  {
    label: "Dashboard",
    icon: DashboardIcon,
    route: "/Dashboard",
    color: "#4FD1C5",
  },
  {
    label: "Bookings",
    icon: DocumentIcon,
    color: "#4FD1C5",
    children: [
      { label: "Funeral", route: "/bookings/funeral" },
      { label: "Event", route: "/bookings/event" },
      { label: "Conference", route: "/bookings/conference" },
      { label: "Church", route: "/bookings/church" },
      { label: "Funeral Home", route: "/bookings/funeral-home" },
      { label: "Other", route: "/bookings/other" },
    ],
  },
  {
    label: "Contacts",
    icon: ContactIcon,
    route: "/contacts",
    color: "#63B3ED",
  },
  {
    label: "Venues",
    icon: ContactIcon,
    route: "/venues",
    color: "#718096",
  },
  {
    label: "All Crew",
    icon: AllCrewIcon,
    route: "/crew-management/all-crew",
    color: "#F6AD55",
  },
  {
    label: "Streams",
    icon: OperationsIcon,
    route: "/stream-management",
    color: "#4299E1",
  },
  {
    label: "Requested Quotes",
    icon: DocumentIcon,
    route: "/request-for-quote",
    color: "#ED64A6",
  },
  {
    label: "Memorial Pages",
    icon: DocumentIcon,
    route: "/client-portal-dashboard",
    color: "#48BB78",
  },
  {
    label: "Proposal",
    icon: DocumentIcon,
    route: "/proposals",
    color: "#F687B3",
  },
  {
    label: "Figma Designs",
    icon: FigmaLinkIcon,
    route: "/figma-designs",
    color: "#9F7AEA",
  },
  {
    label: "Tool and Assets",
    icon: GridViewOutlined,
    action: "OPEN_TOOLS_DRAWER" as const,
    color: "#10B981",
  },
  {
    label: "Configurations",
    icon: ConfigurationIcon,
    route: "/configurations",
    color: "#ED8936",
  },
  {
    label: "User Admin",
    icon: UserAdminIcon,
    route: "/user-management",
    color: "#667EEA",
  },
  {
    label: "Endpoint Request",
    icon: EndpointRequestIcon,
    route: "/endpoint-requests",
    color: "#D53F8C",
  },
  {
    label: "Bug Reports",
    icon: BugReport,
    route: "/bug-reports",
    color: "#e60707",
  },
  {
    label: "Memory Table",
    icon: MemoryIcon,
    route: "/memory-table",
    color: "#FC8181",
  },
];

export type StoredCustomApp = {
  label: string;
  route: string;
  color: string;
  iconKey: string;
};

const STORAGE_KEY = "app_launcher_custom_apps";

export function useCustomApps() {
  const [customApps, setCustomApps] = useState<StoredCustomApp[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredCustomApp[]) : [];
    } catch {
      return [];
    }
  });

  

  const addCustomApp = (app: StoredCustomApp) => {
    setCustomApps((prev) => {
      const next = [...prev, app];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const removeCustomApp = (label: string) => {
    setCustomApps((prev) => {
      const next = prev.filter((a) => a.label !== label);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;

      try {
        const newValue = event.newValue;
        if (!newValue) return;

        setCustomApps(JSON.parse(newValue));
      } catch {
        console.log("failed")
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return { customApps, addCustomApp, removeCustomApp };
}
